import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Button,
    Tabs,
    Tab,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Alert,
    CircularProgress,
    Chip
} from '@mui/material';
import {
    Close as CloseIcon,
    Warning as WarningIcon,
    Edit as EditIcon,
    Stop as StopIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { fullUpdateStrategy, adminOverrideStop } from '../../api/admin_api';

/**
 * B1: EditStrategyModal Component
 * Allows Admin to edit existing strategies with safety checks for running strategies
 *
 * Per ADMIN_IMPLEMENTATION.md Section B:
 * - Tabs: Basic Info | Symbols | Parameters
 * - Pre-fill with existing strategy data
 * - Conditional field locking (if running/has positions)
 */

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const EditStrategyModal = ({ open, onClose, strategy, onSave }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [stopping, setStopping] = useState(false);
    const token = localStorage.getItem('token');

    // Form data state (pre-filled from strategy)
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        dashboardType: 'Algo.01',
        accountNumber: '',
        futuresSymbol: '',
        spotSymbol: '',
        // Parameters
        gridThreshold: '',
        maxGridLevels: '',
        buyGrid: '',
        sellGrid: '',
        buyBaseLotSize: '',
        sellBaseLotSize: '',
        buyLotSizeMultiplier: '',
        sellLotSizeMultiplier: '',
        maxLotAllowed: '',
        isEnabled: true
    });

    // B4: Safety check states
    const isRunning = strategy?.status === 'running' || strategy?.status === 'Running';
    const hasOpenPositions = (strategy?.openPositions || 0) > 0;

    // Initialize form data when strategy changes
    useEffect(() => {
        if (strategy && open) {
            const params = strategy.parameters || {};

            // Helper to get nested parameter value
            const getParam = (key, fallback = '') => {
                const keys = key.split('.');
                let value = params;
                for (const k of keys) {
                    if (value && typeof value === 'object' && k in value) {
                        value = value[k];
                    } else {
                        return fallback;
                    }
                }
                return value ?? fallback;
            };

            setFormData({
                name: strategy.name || '',
                description: strategy.description || '',
                dashboardType: strategy.dashboardType || 'Algo.01',
                accountNumber: strategy.accountNumber || '',
                futuresSymbol: getParam('FuturesSymbol', strategy.futuresSymbol || (strategy.symbols?.[0]) || ''),
                spotSymbol: getParam('SpotSymbol', strategy.spotSymbol || (strategy.symbols?.[1]) || ''),
                gridThreshold: getParam('GridManagement.GridThreshold', ''),
                maxGridLevels: getParam('GridManagement.MaxGridLevels', ''),
                buyGrid: getParam('GridManagement.BuyGrid', ''),
                sellGrid: getParam('GridManagement.SellGrid', ''),
                buyBaseLotSize: getParam('GridManagement.BuyBaseLotSize', ''),
                sellBaseLotSize: getParam('GridManagement.SellBaseLotSize', ''),
                buyLotSizeMultiplier: getParam('GridManagement.BuyLotSizeMultiplier', ''),
                sellLotSizeMultiplier: getParam('GridManagement.SellLotSizeMultiplier', ''),
                maxLotAllowed: getParam('GridManagement.MaxLotOpenedAllowed', ''),
                isEnabled: strategy.isEnabled !== false
            });
        }
    }, [strategy, open]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // B4: Stop strategy before editing
    const handleStopAndEdit = async () => {
        if (!strategy?.id) return;

        setStopping(true);
        try {
            await adminOverrideStop(token, strategy.id);
            toast.success('Strategy stopped. You can now edit.');
            // Refresh strategy data
            if (onSave) {
                onSave(null, true); // Pass flag to refresh
            }
        } catch (error) {
            console.error('Error stopping strategy:', error);
            toast.error('Failed to stop strategy');
        } finally {
            setStopping(false);
        }
    };

    // Save changes
    const handleSave = async () => {
        if (!strategy?.id) return;

        setLoading(true);
        try {
            // Build update payload
            const updateData = {
                name: formData.name,
                description: formData.description,
                dashboardType: formData.dashboardType,
                isEnabled: formData.isEnabled,
                parameters: {
                    FuturesSymbol: formData.futuresSymbol,
                    SpotSymbol: formData.spotSymbol,
                    'GridManagement.GridThreshold': formData.gridThreshold,
                    'GridManagement.MaxGridLevels': formData.maxGridLevels,
                    'GridManagement.BuyGrid': formData.buyGrid,
                    'GridManagement.SellGrid': formData.sellGrid,
                    'GridManagement.BuyBaseLotSize': formData.buyBaseLotSize,
                    'GridManagement.SellBaseLotSize': formData.sellBaseLotSize,
                    'GridManagement.BuyLotSizeMultiplier': formData.buyLotSizeMultiplier,
                    'GridManagement.SellLotSizeMultiplier': formData.sellLotSizeMultiplier,
                    'GridManagement.MaxLotOpenedAllowed': formData.maxLotAllowed
                }
            };

            console.log('[EditStrategyModal] Saving strategy:', {
                strategyId: strategy.id,
                updateData
            });

            const result = await fullUpdateStrategy(token, strategy.id, updateData);

            if (result.success) {
                toast.success('Strategy updated successfully');
                if (onSave) {
                    onSave(updateData);
                }
                onClose();
            } else {
                throw new Error(result.message || 'Failed to update strategy');
            }
        } catch (error) {
            console.error('Error updating strategy:', error);
            toast.error(error.message || 'Failed to update strategy');
        } finally {
            setLoading(false);
        }
    };

    if (!strategy) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    background: '#1e1e1e',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    maxHeight: '90vh'
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#252525',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '16px 24px'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EditIcon sx={{ color: '#667eea' }} />
                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                        Edit Strategy: {strategy.name}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ padding: 3 }}>
                {/* B4: Warning for running strategy */}
                {isRunning && (
                    <Alert
                        severity="warning"
                        icon={<WarningIcon />}
                        sx={{ marginBottom: 3 }}
                        action={
                            <Button
                                color="warning"
                                size="small"
                                startIcon={stopping ? <CircularProgress size={16} /> : <StopIcon />}
                                onClick={handleStopAndEdit}
                                disabled={stopping}
                            >
                                {stopping ? 'Stopping...' : 'Stop & Edit'}
                            </Button>
                        }
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Strategy is currently running
                        </Typography>
                        <Typography variant="caption">
                            Some fields are locked while the strategy is running. Stop the strategy to edit all fields.
                        </Typography>
                    </Alert>
                )}

                {/* B4: Warning for open positions */}
                {hasOpenPositions && (
                    <Alert severity="info" sx={{ marginBottom: 3 }}>
                        <Typography variant="body2">
                            This strategy has {strategy.openPositions} open position(s). Symbol and Account fields are locked.
                        </Typography>
                    </Alert>
                )}

                {/* Tabs */}
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        '& .MuiTab-root': {
                            color: 'rgba(255, 255, 255, 0.6)',
                            '&.Mui-selected': {
                                color: '#667eea'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#667eea'
                        }
                    }}
                >
                    <Tab label="Basic Info" />
                    <Tab label="Symbols" />
                    <Tab label="Parameters" />
                </Tabs>

                {/* Tab 1: Basic Info */}
                <TabPanel value={activeTab} index={0}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Strategy Name"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                disabled={isRunning}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                multiline
                                rows={3}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Dashboard Type</InputLabel>
                                <Select
                                    value={formData.dashboardType}
                                    label="Dashboard Type"
                                    onChange={(e) => handleChange('dashboardType', e.target.value)}
                                    disabled={isRunning}
                                    sx={{
                                        color: '#ffffff',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    }}
                                >
                                    <MenuItem value="Algo.01">Algo.01 - Legacy</MenuItem>
                                    <MenuItem value="Algo.02">Algo.02 - Analytics</MenuItem>
                                    <MenuItem value="Algo.03">Algo.03 - Reserved</MenuItem>
                                    <MenuItem value="Algo.04">Algo.04 - Reserved</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Account Number"
                                value={formData.accountNumber}
                                disabled={true} // Account cannot be changed
                                helperText="Account cannot be changed after creation"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                                    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' }
                                }}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 2: Symbols */}
                <TabPanel value={activeTab} index={1}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Futures Symbol"
                                value={formData.futuresSymbol}
                                onChange={(e) => handleChange('futuresSymbol', e.target.value)}
                                disabled={hasOpenPositions || isRunning}
                                helperText={hasOpenPositions ? "Cannot change with open positions" : ""}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Spot Symbol"
                                value={formData.spotSymbol}
                                onChange={(e) => handleChange('spotSymbol', e.target.value)}
                                disabled={hasOpenPositions || isRunning}
                                helperText={hasOpenPositions ? "Cannot change with open positions" : ""}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        {strategy.symbols && strategy.symbols.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
                                    Current Symbols:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {strategy.symbols.map((symbol, idx) => (
                                        <Chip
                                            key={idx}
                                            label={symbol}
                                            size="small"
                                            sx={{
                                                bgcolor: 'rgba(102, 126, 234, 0.2)',
                                                color: '#667eea'
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </TabPanel>

                {/* Tab 3: Parameters */}
                <TabPanel value={activeTab} index={2}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Grid Threshold"
                                type="number"
                                value={formData.gridThreshold}
                                onChange={(e) => handleChange('gridThreshold', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Max Grid Levels"
                                type="number"
                                value={formData.maxGridLevels}
                                onChange={(e) => handleChange('maxGridLevels', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Buy Grid"
                                type="number"
                                value={formData.buyGrid}
                                onChange={(e) => handleChange('buyGrid', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sell Grid"
                                type="number"
                                value={formData.sellGrid}
                                onChange={(e) => handleChange('sellGrid', e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Buy Base Lot Size"
                                type="number"
                                value={formData.buyBaseLotSize}
                                onChange={(e) => handleChange('buyBaseLotSize', e.target.value)}
                                inputProps={{ step: '0.01' }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sell Base Lot Size"
                                type="number"
                                value={formData.sellBaseLotSize}
                                onChange={(e) => handleChange('sellBaseLotSize', e.target.value)}
                                inputProps={{ step: '0.01' }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Buy Lot Multiplier"
                                type="number"
                                value={formData.buyLotSizeMultiplier}
                                onChange={(e) => handleChange('buyLotSizeMultiplier', e.target.value)}
                                inputProps={{ step: '0.1' }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sell Lot Multiplier"
                                type="number"
                                value={formData.sellLotSizeMultiplier}
                                onChange={(e) => handleChange('sellLotSizeMultiplier', e.target.value)}
                                inputProps={{ step: '0.1' }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Max Lot Allowed"
                                type="number"
                                value={formData.maxLotAllowed}
                                onChange={(e) => handleChange('maxLotAllowed', e.target.value)}
                                inputProps={{ step: '0.01' }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' }
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                                }}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>
            </DialogContent>

            <DialogActions sx={{
                padding: 3,
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                background: '#252525',
                gap: 1.5
            }}>
                <Button
                    onClick={onClose}
                    sx={{
                        textTransform: 'none',
                        color: 'rgba(255, 255, 255, 0.7)'
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || isRunning}
                    startIcon={loading ? <CircularProgress size={16} /> : <EditIcon />}
                    sx={{
                        textTransform: 'none',
                        background: '#667eea',
                        '&:hover': {
                            background: '#5568d3'
                        },
                        '&:disabled': {
                            background: 'rgba(102, 126, 234, 0.3)'
                        }
                    }}
                >
                    {loading ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditStrategyModal;
